export class ArcChart {
  constructor(element, config = {}) {
    this.chartId = 'arc-chart-' + Math.random().toString(36).substr(2, 9);

    this.element = element;
    this.data = element.dataset.chartArcs
      .split(",")
      .map((n) => parseInt(n.trim()))
      .filter((n) => !isNaN(n));

    this.maxValue = parseInt(element.dataset.chartArcsMax) || 100;

    // Parse colors and check for gradient
    const attrColors = element.dataset.chartArcsColors;
    const parsedColors = attrColors
      ? attrColors.split(",").map((c) => c.trim())
      : null;

    this.useGradient = element.hasAttribute("data-chart-gradient");
    this.startColor = element.dataset.chartStartColor || null;
    this.hasCounter = element.hasAttribute("data-chart-counter");
    this.counterValue = this.hasCounter
      ? parseInt(element.dataset.chartCounter) || this.data[0] || 0
      : null;

    const defaultConfig = {
      colors: ["#D3FFA6", "#34D3FF", "#EFE1D6"],
      placeholderColor: "#A1EBFF",
      placeholderBackgroundColor: "none",
      placeholderOutlineWidth: 1,
      gradientOpacityStart: 0.1,
      gradientOpacityEnd: 1,
      animationDuration: 1,
      animationDelay: 0.2,
      arcThickness: 25,
      arcSpacing: null,
      centerX: 185,
      centerY: 185,
      maxRadius: 170,
      precision: 2,
      counterClass: "arc-chart-counter",
      counterWrapperClass: "arc-chart-counter-wrapper",
      counterValueClass: "arc-chart-counter-value",
      counterSymbolClass: "arc-chart-counter-symbol",
      intersectionThreshold: 0.2,
      intersectionRootMargin: "50px",
    };

    this.config = { ...defaultConfig, ...config };

    if (parsedColors) {
      this.config.colors = parsedColors;
    }

    if (this.config.arcSpacing === null) {
      this.config.arcSpacing = this.config.arcThickness / 2;
    }

    this.hasAnimated = false;
    this.paths = [];
    this.counterElement = null;

    this.setupIntersectionObserver();
    this.setup();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: this.config.intersectionRootMargin,
      threshold: this.config.intersectionThreshold
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasAnimated) {
          this.startAnimations();
          this.hasAnimated = true;
          this.observer.disconnect();
        }
      });
    }, options);
  }

  setup() {
    const container = document.createElement("div");
    container.style.position = "relative";
    container.style.width = "100%";

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 370 186");
    svg.setAttribute("width", "100%");
    svg.style.display = "block";
    svg.style.shapeRendering = "geometricPrecision";
    svg.style.textRendering = "geometricPrecision";

    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    if (this.useGradient) {
      this.data.forEach((_, index) => {
        const gradient = this.createGradient(index);
        defs.appendChild(gradient);
      });
    }
    svg.appendChild(defs);

    const mainGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
    mainGroup.setAttribute("shape-rendering", "geometricPrecision");

    const numArcs = this.data.length;
    const radii = this.calculateRadii(numArcs);

    radii.forEach((radius) => {
      const bgGroup = this.createBackgroundArc(radius);
      mainGroup.appendChild(bgGroup);
    });

    radii.forEach((radius, index) => {
      const filledArc = this.createFilledArc(
        radius,
        this.data[index],
        index
      );
      this.paths.push(filledArc);
      mainGroup.appendChild(filledArc);
    });

    svg.appendChild(mainGroup);
    container.appendChild(svg);

    if (this.hasCounter) {
      const counterWrapper = document.createElement("div");
      counterWrapper.className = this.config.counterWrapperClass;

      const counter = document.createElement("div");
      counter.className = this.config.counterClass;

      const numberWrapper = document.createElement("span");
      numberWrapper.className = this.config.counterValueClass;
      numberWrapper.textContent = "0";

      const symbol = document.createElement("span");
      symbol.className = this.config.counterSymbolClass;
      symbol.textContent = "%";

      counter.appendChild(numberWrapper);
      counter.appendChild(symbol);
      counterWrapper.appendChild(counter);
      container.appendChild(counterWrapper);

      this.counterElement = numberWrapper;
    }

    this.element.appendChild(container);
    this.observer.observe(container);
  }

  createGradient(index) {
    const gradient = document.createElementNS("http://www.w3.org/2000/svg", "linearGradient");
    const id = `${this.chartId}-gradient-${index}`;
    gradient.setAttribute("id", id);

    gradient.setAttribute("x1", "0%");
    gradient.setAttribute("y1", "0%");
    gradient.setAttribute("x2", "100%");
    gradient.setAttribute("y2", "0%");
    gradient.setAttribute("gradientUnits", "userSpaceOnUse");

    const angle = -45;
    gradient.setAttribute("gradientTransform",
      `rotate(${angle}, ${this.config.centerX}, ${this.config.centerY})`
    );

    const color = this.config.colors[index] || this.config.colors[0];

    const stop1 = document.createElementNS("http://www.w3.org/2000/svg", "stop");
    stop1.setAttribute("offset", "0%");

    // Use start color if provided, otherwise use base color with opacity
    if (this.startColor) {
      stop1.setAttribute("stop-color", this.startColor);
      stop1.setAttribute("stop-opacity", this.config.gradientOpacityStart);
    } else {
      stop1.setAttribute("stop-color", color);
      stop1.setAttribute("stop-opacity", this.config.gradientOpacityStart);
    }


    const endValue = this.data[index];
    const percentage = (endValue / this.maxValue) * 100;
    const stop2 = document.createElementNS("http://www.w3.org/2000/svg", "stop");
    stop2.setAttribute("offset", `${percentage}%`);
    stop2.setAttribute("stop-color", color);
    stop2.setAttribute("stop-opacity", this.config.gradientOpacityEnd);

    gradient.appendChild(stop1);
    gradient.appendChild(stop2);

    return gradient;
  }

  startAnimations() {
    this.paths.forEach((path) => {
      requestAnimationFrame(() => {
        path.style.strokeDashoffset = "0";
      });
    });

    if (this.hasCounter && this.counterElement) {
      this.animateCounter(this.counterElement, this.counterValue);
    }
  }

  calculateRadii(numArcs) {
    const radii = [];
    for (let i = 0; i < numArcs; i++) {
      const radius = this.config.maxRadius - i * (this.config.arcThickness + this.config.arcSpacing);
      radii.push(radius);
    }
    return radii;
  }

  createBackgroundArc(radius) {
    const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
    group.setAttribute("shape-rendering", "geometricPrecision");

    const outlinePath = this.describeArc(
      this.config.centerX,
      this.config.centerY,
      radius,
      -90,
      90
    );

    const bgArc = document.createElementNS("http://www.w3.org/2000/svg", "path");
    bgArc.setAttribute("d", outlinePath);
    bgArc.setAttribute("fill", "none");
    bgArc.setAttribute("stroke", this.config.placeholderBackgroundColor);
    bgArc.setAttribute("stroke-width", this.config.arcThickness + (this.config.placeholderOutlineWidth * 2));
    bgArc.setAttribute("stroke-linecap", "butt");
    bgArc.setAttribute("shape-rendering", "geometricPrecision");
    group.appendChild(bgArc);

    const outlineArc1 = document.createElementNS("http://www.w3.org/2000/svg", "path");
    outlineArc1.setAttribute("d", this.describeArc(
      this.config.centerX,
      this.config.centerY,
      radius - (this.config.arcThickness / 2),
      -90,
      90
    ));
    outlineArc1.setAttribute("fill", "none");
    outlineArc1.setAttribute("stroke", this.config.placeholderColor);
    outlineArc1.setAttribute("stroke-width", this.config.placeholderOutlineWidth);
    outlineArc1.setAttribute("stroke-linecap", "butt");
    outlineArc1.setAttribute("shape-rendering", "geometricPrecision");

    const outlineArc2 = document.createElementNS("http://www.w3.org/2000/svg", "path");
    outlineArc2.setAttribute("d", this.describeArc(
      this.config.centerX,
      this.config.centerY,
      radius + (this.config.arcThickness / 2),
      -90,
      90
    ));
    outlineArc2.setAttribute("fill", "none");
    outlineArc2.setAttribute("stroke", this.config.placeholderColor);
    outlineArc2.setAttribute("stroke-width", this.config.placeholderOutlineWidth);
    outlineArc2.setAttribute("stroke-linecap", "butt");
    outlineArc2.setAttribute("shape-rendering", "geometricPrecision");

    // Add start vertical line (at -90 degrees)
    const startPoint = this.polarToCartesian(this.config.centerX, this.config.centerY, radius + (this.config.arcThickness / 2), -90);
    const startEndPoint = this.polarToCartesian(this.config.centerX, this.config.centerY, radius - (this.config.arcThickness / 2), -90);
    const startLine = document.createElementNS("http://www.w3.org/2000/svg", "line");
    startLine.setAttribute("x1", startPoint.x);
    startLine.setAttribute("y1", startPoint.y);
    startLine.setAttribute("x2", startEndPoint.x);
    startLine.setAttribute("y2", startEndPoint.y);
    startLine.setAttribute("stroke", this.config.placeholderColor);
    startLine.setAttribute("stroke-width", this.config.placeholderOutlineWidth);
    startLine.setAttribute("shape-rendering", "geometricPrecision");

    // Add end vertical line (at 90 degrees)
    const endPoint = this.polarToCartesian(this.config.centerX, this.config.centerY, radius + (this.config.arcThickness / 2), 90);
    const endEndPoint = this.polarToCartesian(this.config.centerX, this.config.centerY, radius - (this.config.arcThickness / 2), 90);
    const endLine = document.createElementNS("http://www.w3.org/2000/svg", "line");
    endLine.setAttribute("x1", endPoint.x);
    endLine.setAttribute("y1", endPoint.y);
    endLine.setAttribute("x2", endEndPoint.x);
    endLine.setAttribute("y2", endEndPoint.y);
    endLine.setAttribute("stroke", this.config.placeholderColor);
    endLine.setAttribute("stroke-width", this.config.placeholderOutlineWidth);
    endLine.setAttribute("shape-rendering", "geometricPrecision");

    group.appendChild(outlineArc1);
    group.appendChild(outlineArc2);
    group.appendChild(startLine);
    group.appendChild(endLine);

    return group;
  }

  createFilledArc(radius, value, index) {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    const percentage = (value / this.maxValue) * 100;
    const endAngle = -90 + percentage * 1.8;

    const d = this.describeArc(
      this.config.centerX,
      this.config.centerY,
      radius,
      -90,
      endAngle
    );

    path.setAttribute("d", d);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", this.useGradient
      ? `url(#${this.chartId}-gradient-${index})`  // Use chartId in gradient reference
      : this.config.colors[index] || this.config.colors[0]
    );
    path.setAttribute("stroke-width", this.config.arcThickness);
    path.setAttribute("stroke-linecap", "butt");
    path.setAttribute("shape-rendering", "geometricPrecision");

    const length = path.getTotalLength();
    path.style.strokeDasharray = length;
    path.style.strokeDashoffset = length;

    const delay = index * this.config.animationDelay;
    path.style.transition = `stroke-dashoffset ${this.config.animationDuration}s ease-in-out ${delay}s`;

    return path;
  }

  polarToCartesian(centerX, centerY, radius, angleInDegrees) {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    const x = centerX + (radius * Math.cos(angleInRadians));
    const y = centerY + (radius * Math.sin(angleInRadians));

    return {
      x: Number(x.toFixed(this.config.precision)),
      y: Number(y.toFixed(this.config.precision))
    };
  }

  describeArc(x, y, radius, startAngle, endAngle) {
    const start = this.polarToCartesian(x, y, radius, startAngle);
    const end = this.polarToCartesian(x, y, radius, endAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 1, end.x, end.y
    ].join(" ");
  }

  animateCounter(element, targetValue) {
    const duration = this.config.animationDuration * 1000;
    const startTime = performance.now();
    const startValue = 0;

    const updateCounter = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = this.easeOutQuad(progress);
      const currentValue = Math.round(
        startValue + (targetValue - startValue) * easeProgress
      );

      element.textContent = currentValue;

      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      }
    };

    requestAnimationFrame(updateCounter);
  }

  easeOutQuad(t) {
    return t * (2 - t);
  }

  dispose() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.observer = null;
    this.paths = null;
    this.counterElement = null;
    this.element = null;
    this.config = null;
    this.data = null;
  }
}
